import { DatePipe, formatNumber } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    Output,
    ViewChild,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import {
    faDownload,
    faInfoCircle,
    faSort,
    faPaperclip,
    faSyncAlt,
    faLock
} from "@fortawesome/free-solid-svg-icons";
import { MenuItem } from "primeng/api";
import { Table as PrimeTable } from "primeng/table";
import { CsvGeneratorService } from "../../services/shared/csv-generator-service";
import { ImageViewerService } from "../../services/shared/image-viewer-service";
import {
    ButtonOption,
    DataTableColumn,
    DataTableColumnType,
    FilterOption,
} from "./datatablecolumn/datatablecolumn.component";

@Component({
    standalone: false,
    selector: "data-table",
    templateUrl: "./datatable.component.html",
})
export class DataTable implements OnChanges {
    @Input() dataset: any[] = [];
    @Input() enableFilter = false;
    @Input() addThumbnailPrefix = true;
    @Input() openImageInNewWindow = false;
    @Input() defaultSort = null;
    @Input() sortOrder: number = 1;
    @Input() scrollable: boolean = true;
    @Input() scrollHeight: string = null;
    @Input() scrollWidth: string = null;
    @Input() rowGroupMode: string = "";
    @Input() groupField: string = null;
    @Input() expandableRowGroups: boolean = false;
    @Input() sortableRowGroup: boolean = false;
    @Input() hideSelectAll: boolean = false;
    @Input() expandedRowsGroups: any[] = [];
    @Input() selectionMode: string = null;
    @Input() frozenWidth: string = null;
    @Input() paginator: boolean = true;
    @Input() emptyMessageString: string = "No records found";
    @Input() rowsPerPage: Array<number> = [10, 25, 50, 100, 500, 1000];
    @Input() rowsToDisplay: number = 10;
    @Input() name: string = null;
    @Input() showExport: boolean = true;
    @Input() pageNumber: number = 0;
    @Input() loading: boolean = false;
    @Input() isFromFormRegister: boolean = false;
    @Input() showCaption: boolean = false;
    @Input() showClearFilter: boolean = false;
    @Input() showrREQFilter: boolean = false;
    @Input() isRegisterFilterLocked: boolean = false;
    @Input() isProcurement: boolean = false;
    @Input() showFilter: boolean = false;
    @Input() tableCount: string = "1";

    @Output() onImageUpdated: EventEmitter<any> = new EventEmitter<any>();
    @Output() onRowSelected: EventEmitter<any> = new EventEmitter<any>();
    @Output() onRowUnselected: EventEmitter<any> = new EventEmitter<any>();
    @Output()
    onHeaderCheckboxClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() change: EventEmitter<any> = new EventEmitter<any>();
    @Output() onRowChanged: EventEmitter<any> = new EventEmitter<any>(true);
    @Output() pageNumberChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() onOpenFilterRegisterModalButtonClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() onTableRefreshButtonClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() onClearFilterButtonClicked: EventEmitter<any> = new EventEmitter<any>();
    @Output() onSearchChange: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild("dt") public table: PrimeTable;

    columns: DataTableColumn[] = [];
    private csvGeneratorService: CsvGeneratorService;
    private autoWidth: string = "";
    private autoMaxWidth: string = "";
    private frozenTotalWidth: number = null;
    private highlightColour: string = "";
    private rowSelected: boolean = false;
    private changeDetectorRef: ChangeDetectorRef;

    imageViewerService: ImageViewerService;
    thumbnailApiUrl: string = "/api/thumbnail/300?file=";
    faDownload = faDownload;
    faInfoSign = faInfoCircle;
    faSort = faSort;
    faPaperclip = faPaperclip;
    faRefresh = faSyncAlt;
    faLock = faLock;
    menuItems: MenuItem[] = [];

    selectedReqs: any[] = [];
    plantEntries: any[] = [];
    searchValueText: string = "";
    searchReqIdFilteredPlantEntries: any[] = [];
    currentDataTableColumnList: any[] = [];
    yearRange: string;

    // UI only: long text cells are truncated with a Show More/Less toggle; row data is untouched
    readonly textTruncateLength = 150;
    private expandedTextCells = new WeakMap<object, Set<string>>();

    constructor(
        private sanitizer: DomSanitizer,
        private elementRef: ElementRef,
        imageViewerService: ImageViewerService,
        csvGeneratorService: CsvGeneratorService,
        changeDetectorRef: ChangeDetectorRef
    ) {
        this.imageViewerService = imageViewerService;
        this.csvGeneratorService = csvGeneratorService;
        this.changeDetectorRef = changeDetectorRef;
    }

    ngOnInit() {
        if ((this.columns || []).length > 0) this.columns[0].frozen = true;
    }

    ngAfterViewInit(): void {
        this.onResize();
        this.setMinMaxDate();
        this.setYear();

        if (!this.changeDetectorRef["destroyed"])
            this.changeDetectorRef.detectChanges();
    }

    preventDefault($event) {
        $event.preventDefault();
        $event.stopPropagation();
        return false;
    }

    onInput(event: KeyboardEvent, item: any, columnName: string): void {
        // Allowed key codes: 0-9, decimal point (if needed), backspace, delete, arrow keys, Ctrl+A
        let inputValue = (event.target as HTMLInputElement).value;
        const decimalIndex = inputValue.indexOf('.');
        const hasDecimal = decimalIndex !== -1;

        // Check if the input already has a decimal point and if it does, ensure there are no more than 2 digits after it
        if (hasDecimal && inputValue.split('.')[1].length > 2) {
            inputValue = inputValue.split('.')[0] + "." + inputValue.split('.')[1].substring(0, 2);
            item[columnName] = inputValue.toString();
            (event.target as HTMLInputElement).value = inputValue;
            event.preventDefault();
            return;
        }

        // Check if the input value length is already 18
        if (((hasDecimal && inputValue.length - 1 > 16) || (!hasDecimal && inputValue.length > 16))) {
            inputValue = hasDecimal ? inputValue.split('.')[0].substring(0, 15) + "." + inputValue.split('.')[1] : inputValue.split('.')[0].substring(0, 16);
            item[columnName] = inputValue.toString();
            (event.target as HTMLInputElement).value = inputValue;
            event.preventDefault();
            return;
        }
    }

    onKeyDown(event: KeyboardEvent) {
        let inputValue = (event.target as HTMLInputElement).value;
        const decimalIndex = inputValue.indexOf('.');

        // Check for the 'e' character and prevent it
        if (event.key === 'e' || event.key === '-' || event.key === '+') {
            event.preventDefault();
            return;
        }

        // Prevent inputting a decimal point if it's not following digits
        if (event.key === '.' && decimalIndex !== -1 && (decimalIndex === 0 || decimalIndex === inputValue.length - 1)) {
            event.preventDefault();
            return;
        }
    }

    onPaste(event: ClipboardEvent): void {
        const pastedText = event.clipboardData.getData('text');

        // Check if pasted text will exceed 18 characters
        const inputElement = event.target as HTMLInputElement;
        if (pastedText.replace('.', '').length + inputElement.value.replace('.', '').length > 16) {
            event.preventDefault();
        }
    }

    @HostListener("window:resize")
    onResize() {
        var width = 0;
        var columns = this.columns.filter((s) => s.visible || true);
        var columnSizes = columns.map((s) =>
            parseInt(this.calculateWidth(s).replace("px", "") || "0")
        );

        var accumulatedLength = columnSizes.reduce((a, b, index) => a + b, 0);

        var numberOfUnsized = columnSizes.filter((a) => !a).length;

        var totalWidth =
            this.elementRef.nativeElement.offsetWidth ||
            this.elementRef.nativeElement.parentElement.offsetWidth - 30;

        if (accumulatedLength > 0)
            width = (totalWidth - accumulatedLength) / numberOfUnsized;
        else width = totalWidth / numberOfUnsized;

        if (accumulatedLength > totalWidth || width < 200) width = 200;

        this.autoWidth = `${width}px`;
        this.autoMaxWidth = `${width}px`;
    }

    addColumn(column: DataTableColumn) {
        this.columns.push(column);
    }

    getEmptyMessage() {
        return this.emptyMessageString;
    }

    openFilterRegisterModal() {
        this.onOpenFilterRegisterModalButtonClicked.emit();
    }

    onRefreshTable(tableCount) {
        this.onTableRefreshButtonClicked.emit(tableCount);
    }

    onClearFilter() {
        this.onClearFilterButtonClicked.emit();
    }

    onAddItem(event) {
        this.selectedReqs.push(event.value);
        this.refreshPlantEntries();
    }

    onRemoveItem(event) {
        if (this.selectedReqs.find(x => x == event.value)) {
            this.selectedReqs.splice(this.selectedReqs.findIndex(x => x == event.value), 1);
        }
        this.refreshPlantEntries();
    }

    onSearchTextChange(searchValue, tableCount) {
        if (!this.isProcurement)
            this.onSearchChange.emit(searchValue);
        else
            this.searchTextOnPlant(searchValue);
    }

    getRowsPerPage() {
        return this.rowsPerPage;
    }

    ngOnChanges() {

        if (this.isProcurement) {
            this.plantEntries = this.dataset;
            this.searchReqIdFilteredPlantEntries = this.dataset;
            this.refreshPlantEntries();
            if (this.searchValueText.length > 0) {
                this.onSearchTextChange(this.searchValueText, "1")
            }

            this.currentDataTableColumnList = this.columns
                .filter(
                    (column) =>
                        column.type != DataTableColumnType.CheckBox &&
                        column.type != "CheckBox" &&
                        column.type != DataTableColumnType.Attachments &&
                        column.type != "Attachments" &&
                        column.value &&
                        column.visible != false
                )
                .map((column) => {
                    return {
                        value: column.value,
                        header: column.header,
                        type: this.convertColumnType(column.type),
                    };
                });
        }

        let tempDisabled = this.columns.filter((x) => x.disableCheck);
        if (tempDisabled.length > 0) {
            for (var i = 0; i < tempDisabled.length; i++) {
                let column = tempDisabled[i];
                this.disableCellsInSet(column.value, column.disableCheck);
            }
        }
    }

    onRowSelect(event) {
        if (this.columns.find((item) => item.selectionMode == "single")) {
            (this.dataset || []).forEach((value) => {
                value.selected = false;
            });
        }

        event.data.selected = true;

        if (this.onRowSelected) this.onRowSelected.emit(event);
    }

    onRowUnselect(event) {
        event.data.selected = false;
        if (this.onRowUnselected) this.onRowUnselected.emit(event);
    }

    onHeaderCheckboxToggle(event) {
        if (this.table) {
            let data = this.table.filteredValue || this.table.value || [];

            data.forEach((x) => (x.selected = event.checked));

            if (this.onHeaderCheckboxClicked)
                this.onHeaderCheckboxClicked.emit(event);
        }
    }

    onPage(event) {
        this.pageNumberChange.emit(event.first);
    }

    textAreaChanged(control: KeyboardEvent | any, item: any): void {
        if (this.onRowChanged) this.onRowChanged.emit(item);
    }

    dropdownChanged(control: KeyboardEvent | any, item: any): void {
        if (this.onRowChanged) this.onRowChanged.emit(item);
    }

    dateChanged(control: KeyboardEvent | any, item: any): void {
        if (this.onRowChanged) this.onRowChanged.emit(item);
    }

    numberChanged(control: KeyboardEvent | any, item: any): void {
        if (this.onRowChanged) this.onRowChanged.emit(item);
    }

    imageChanged(newPath: string, item: any, column: DataTableColumn): void {
        item[column.value] = event;

        if (this.onImageUpdated)
            this.onImageUpdated.emit({
                newPath,
                item,
                column,
            });
        if (this.change) this.change.emit(this);
        if (this.onRowChanged) this.onRowChanged.emit(item);
    }

    buttonSelectClick(row: any, colIndex: number, key: string) {
        let newValue: string;
        let currentValue: string = row[key];
        let indexOfCurrentValue: number = 0;
        let indexOfNewValue: number = 0;
        let options: any[] = this.columns[colIndex].options as ButtonOption[];

        for (var i = 0; i < options.length; i++) {
            let option = options[i];
            if (option == currentValue) {
                indexOfCurrentValue = i;
                break;
            }
        }

        if (indexOfCurrentValue + 1 < options.length) {
            indexOfNewValue = indexOfCurrentValue + 1;
        }

        newValue = options[indexOfNewValue];

        row[key] = newValue;
    }

    disabledCheck(item: any, column: DataTableColumn) {
        if (item.disabled) return true;

        if (
            item.disabledCells != null &&
            item.disabledCells.some((p) => p == column.value)
        )
            return true;

        return false;
    }

    getLinkName(item: any, url: string): string {
        if (
            item.originalFileName ||
            (item.fileName && item.fileName.indexOf("/") > -1)
        )
            return item.originalFileName || item.fileName;
        else if (url.lastIndexOf("/") > -1)
            return url.substring(url.lastIndexOf("/") + 1);
        else if (item.urlLoadingText) return item.urlLoadingText;
        else return "Download";
    }

    getFileUrlName(url: string): string {
        if (url.lastIndexOf("/") > -1)
            return url.substring(url.lastIndexOf("/") + 1).substring(37);
        else return "";
    }

    getColumnHref(item, column) {
        var url = item[column.value] || "#";
        var urlReplaced = url
            .replace("%", "%25")
            .replace("+", "%2B")
            .replace("#", "%23");

        return this.sanitizer.bypassSecurityTrustUrl(urlReplaced);
    }

    getColumnSrc(item, column, showThumbnail: boolean = false) {
        var secureUrl = this.sanitizer.bypassSecurityTrustUrl(
            item[column.value]
        );

        var path = item[column.value] as string;

        if (showThumbnail)
            path = (item.fileUrl || item[column.value]) as string;

        var query = "";
        var queryIndex = path.indexOf("?");

        if (queryIndex > -1) {
            query = "&" + path.substr(queryIndex + 1);
            path = path.substr(0, queryIndex);
        }

        let encoded = encodeURIComponent(path);

        if (!showThumbnail)
            return this.addThumbnailPrefix && !this.openImageInNewWindow
                ? this.thumbnailApiUrl + item[column.value]
                : secureUrl;
        else return this.thumbnailApiUrl + encoded + query;
    }

    getMultiImageSrcLink(image) {
        let imageUrl
        //imageUrl will be null for entry from web
        if (image.fileUrl)
            imageUrl = image.fileUrl;
        else
            imageUrl = image.fileName;

        var url = imageUrl || "#";
        var urlReplaced = url
            .replace("%", "%25")
            .replace("+", "%2B")
            .replace("#", "%23");

        var secureUrl = this.sanitizer.bypassSecurityTrustUrl(urlReplaced);

        return secureUrl;
    }

    getMultiImageSrc(image, column) {
        let thumbnailApiUrlTemp =
            "/api/thumbnail/" +
            column.imageWidth +
            "/" +
            column.imageHeight +
            "?file=";

        var secureUrl = this.sanitizer.bypassSecurityTrustUrl(
            image.fileUrl || image.fileName
        );

        let encoded = encodeURIComponent(image.fileUrl || image.fileName);

        return thumbnailApiUrlTemp + encoded;
    }

    getMultiHyperlinkSrcLink(link) {
        let fileUrl
        //fileUrl will be null for entry from web
        if (link.fileUrl)
            fileUrl = link.fileUrl;
        else
            fileUrl = link.fileName;

        var url = fileUrl || "#";
        var urlReplaced = url
            .replace("%", "%25")
            .replace("+", "%2B")
            .replace("#", "%23");

        var secureUrl = this.sanitizer.bypassSecurityTrustUrl(urlReplaced);

        return secureUrl;
    }

    getMultiUrlSrcLink(fileUrl) {
        var url = fileUrl || "#";
        var urlReplaced = url
            .replace("%", "%25")
            .replace("+", "%2B")
            .replace("#", "%23");

        var secureUrl = this.sanitizer.bypassSecurityTrustUrl(urlReplaced);

        return secureUrl;
    }

    getViewURL(item, column) {
        var url = item[column.value];

        return url;
    }

    changeModalImage(url, src) {
        if (this.openImageInNewWindow) {
            if (src) {
                window.open(
                    this.sanitizer.bypassSecurityTrustUrl(url) as any,
                    "_blank"
                );
            }
        } else {
            this.imageViewerService.imageUrl =
                this.sanitizer.bypassSecurityTrustUrl(url) as any;
        }
    }

    modelChanged($event, item) {
        if (this.change) this.change.emit(this);
        if (this.onRowChanged) this.onRowChanged.emit(item);
    }

    dateSelected($event: any, item: any, key: string): void {
        item[key] = this.offsetHours(new Date(item[key]));
        this.modelChanged($event, item);
    }

    checkedChange(
        $event: { checked: boolean },
        item,
        columnName,
        disabledCheck
    ) {
        item[columnName] = $event.checked;
        if (disabledCheck) this.disableCellsInSet(columnName, disabledCheck);

        if (this.change) this.change.emit(this);
        if (this.onRowChanged) this.onRowChanged.emit(item);
        if (item[columnName] == true)
            this.table.onRowSelect.emit({ data: item });
        else this.table.onRowUnselect.emit({ data: item });
    }

    disableCellsInSet(columnName, disabledCheck) {
        var allChecked = this.dataset.filter(
            (x) => x[columnName] == true
        ).length;

        for (var i = 0; i < this.dataset.length; i++) {
            let tempItem = this.dataset[i];
            var column = this.columns.find((x) => x.value == columnName);
            if (allChecked >= disabledCheck && tempItem[columnName] != true) {
                tempItem.disabledCells = [columnName];
            } else {
                tempItem.disabledCells = null;
            }

            this.disabledCheck(tempItem, column);
        }
    }

    getSelected() {
        var selectable = this.columns.find(
            (item) =>
                item.selectionMode == "multiple" ||
                item.selectionMode == "single"
        );
        if (selectable)
            return (this.dataset || []).filter((value) => {
                return value[selectable.value];
            });
        else return [];
    }

    getWidth(column: DataTableColumn): string {
        return this.calculateWidth(column);
    }

    calculateWidth(column: DataTableColumn): string {
        if (column.width) return column.width;
        if (
            column.hasOptions ||
            ((column.options || []).length > 0 &&
                "" + column.type != "DropDown" &&
                "" + column.type != "ButtonSelect" &&
                "" + column.type != "Button" &&
                column.type != 5)
        )
            return "50px";

        switch ("" + column.type) {
            case "Options":
            case "7":
            case "ButtonSelect":
            case "Percentage":
            case "8":
                return "100px";
            case "DropDown":
                return column.editable ? "200px" : "100px";
            case "Date":
            case "4":
                return column.filterable ? "120px" : "100px";
            case "CheckBox":
            case "3":
                return "50px";
            default:
                return column.selectionMode == "multiple" ? "50px" : "";
        }
    }

    getOptions(item: any, column: DataTableColumn): ButtonOption[] {
        var options = [];

        if ("" + column.type == "DropDown") {
            if (!column.options && !column.filterOptions) options = [];
            else if (
                (column.filterOptions || {}).constructor === Array &&
                column.filterOptions.length > 0
            )
                options = column.filterOptions as any[];
            else if (
                (column.options || {}).constructor === Array &&
                column.options.length > 0
            )
                options = column.options as any[];
            else if ((item || {})["" + column.filterOptions])
                options = item["" + column.options];
            else if ((item || {})["" + (column.options || "options")])
                options = item["" + (column.options || "options")];
        } else {
            if (!column.options) options = [];
            else if (column.options.constructor === Array)
                options = column.options as any[];
            else if ((item || {})["" + (column.options || "options")])
                options = item["" + (column.options || "options")];
        }

        return options;
    }

    getAttachments(item: any, column: DataTableColumn): FilterOption[] {
        let attachments = [];
        if (item[column.value] && item[column.value].length > 0) {
            (item[column.value] as any[]).forEach((a) => {
                attachments.push({
                    label: a.originalFileName,
                    value: a.fileUrl,
                });
            });
        }

        return attachments;
    }

    getRequired(item: any, column: DataTableColumn): boolean {
        let value = false;

        if (column.required) {
            if (typeof column.required == "function")
                value = column.required(item);
            else value = column.required;
        }

        return value;
    }

    getMinWidth(column: DataTableColumn): string {
        return this.calculateWidth(column) || "200px";
    }

    offsetHours(date: Date): Date {
        return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    }

    getRightPageNumber() {
        var result = 0;
        if (this.table) {
            let displayedRows = this.pageNumber + this.table.rows;

            if (this.dataset.length < displayedRows) {
                result = this.dataset.length;
            } else {
                result = displayedRows;
            }
        } else {
            result = this.pageNumber;
        }

        return result;
    }

    getRightPageNumberForFiltered() {
        var result = 0;

        if (this.table) {
            result = this.table.filteredValue
                ? this.table.filteredValue.length
                : 0;
        }
        return result;
    }

    getWordBreak(column: DataTableColumn): string {
        if (column.type == "Button" || column.type == "HyperLink")
            return "break-word";
        else if (!this.getWidth(column)) return "break-word";
        else return "initial";
    }

    getLeftBuffer(column: DataTableColumn, index: number): string {
        if (!this.isColumnFrozen(column)) return null;

        var frozenSize = 0;

        for (var i = 0; i < index; i++) {
            var currentColumn = this.columns[i];
            if (currentColumn.frozen && currentColumn.visible != false) {
                let minWidth: string = this.getMinWidth(currentColumn);
                if (minWidth) {
                    minWidth = minWidth.substr(0, minWidth.length - 2);
                    frozenSize += +minWidth;
                }
            }
        }

        column.leftBuffer = frozenSize + "px";

        return column.leftBuffer;
    }

    isColumnFrozen(column: DataTableColumn): boolean {
        let isMobileSize = window.innerWidth <= column.mobileFreezeWidth;

        if (!column.frozen || (isMobileSize && column.unfrozenOnMobile == true))
            return false;

        return true;
    }

    isOptionColumn(item: any, column: DataTableColumn): boolean {
        let invalidColumnTypes = ["ButtonSelect", "DropDown", "Button"];

        if (invalidColumnTypes.indexOf(column.type as string) == -1) {
            if (column.hasOptions) return true;
            if (this.getOptions(item, column).length > 0) return true;
        }

        return false;
    }

    hasOptions(column: DataTableColumn): boolean {
        return (
            column.type == DataTableColumnType.Options ||
            column.type == "Options" ||
            column.hasOptions ||
            column.options == "options"
        );
    }

    export(): void {
        if (this.table) {
            let data = this.table.filteredValue || this.table.value || [];
            let headers = this.columns
                .filter(
                    (column) =>
                        column.type != DataTableColumnType.CheckBox &&
                        column.type != "CheckBox" &&
                        column.type != DataTableColumnType.Attachments &&
                        column.type != "Attachments" &&
                        column.value &&
                        column.visible != false
                )
                .map((column) => {
                    return {
                        value: column.value,
                        header: column.header,
                        type: this.convertColumnType(column.type),
                    };
                });

            this.csvGeneratorService.export(
                this.name ? this.name + " export" : "data-export",
                data,
                headers
            );
        }
    }

    convertColumnType(
        type: string | DataTableColumnType
    ): "Date" | "Number" | "Percentage" | "Boolean" | "String" {
        if (type == "Date" || type == DataTableColumnType.Date) return "Date";
        if (type == "Number" || type == DataTableColumnType.Number)
            return "Number";
        if (type == "Percentage" || type == DataTableColumnType.Percentage)
            return "Percentage";
        if (type == "Boolean" || type == DataTableColumnType.Boolean)
            return "Boolean";
        return "String";
    }

    totalColumns(columns: DataTableColumn[]) {
        return columns.some((column) => {
            return column.totalInFooter || false;
        });
    }

    getFooterContent(column: DataTableColumn): string {
        if (!column.totalInFooter) return " ";

        var value = 0;

        if (this.table) {
            let data = this.table.filteredValue || this.table.value || [];

            data.forEach((x) => {
                let temp = x[column.value];
                if (!isNaN(temp)) value += +temp;
            });
        }

        return "Total: " + formatNumber(value, "en-us", column.numberPipe ? column.numberPipe : "1.2-2");
    }

    hasVisibleColumns(row: object, column: DataTableColumn): boolean {
        var options = this.getOptions(row, column);

        return (
            options.filter((x) => {
                if (!x.hide) return true;
                return x.hide(row, this.dataset.length) != true;
            }).length > 0
        );
    }

    hasVisibleAttachments(row: object, column: DataTableColumn): boolean {
        var attachments = this.getAttachments(row, column);

        return attachments.length > 0;
    }

    getMinimumDate(item: any, column: DataTableColumn): boolean {
        let value = null;

        if (column.min != null) {
            if (typeof column.min == "function") value = column.min(item);
            else value = column.min;
        }

        return value;
    }

    getMaximumDate(item: any, column: DataTableColumn): boolean {
        let value = null;

        if (column.max != null) {
            if (typeof column.max == "function") value = column.max(item);
            else value = column.max;
        }

        return value;
    }

    showFooterWithPageCount(): boolean {
        if (!this.paginator) return false;
        else if (!this.dataset) return false;
        else if (!this.table) return false;
        else if (
            !(
                (this.table.filteredValue
                    ? this.table.filteredValue.length
                    : 0) == 0
            )
        )
            return false;
        else if (this.table.value.length <= 0) return false;

        return true;
    }

    getData() {
        return this.table.filteredValue || this.table.value || [];
    }

    showFooterWithoutPageCount(): boolean {
        if (!this.paginator) return false;
        else if (!this.dataset) return false;
        else if (!this.table) return false;
        else if (
            (this.table.filteredValue ? this.table.filteredValue.length : 0) ==
            0
        )
            return false;
        else if (this.table.value.length <= 0) return false;

        return true;
    }

    getRowStyle(column: DataTableColumn, index: number, item) {
        var tempStyle = {};

        var temp = item["changeRowColour"];
        temp != null ? (tempStyle = { "--highlight": temp }) : (tempStyle = {});

        return tempStyle;
    }

    getStyle(column: DataTableColumn, index: number, item) {
        var tempStyle = {};
        if (item) {
            var temp = item[column.value + "_changeColour"];
            temp != null
                ? (tempStyle = { "--highlight": temp })
                : (tempStyle = {});
        }

        return {
            left: this.getLeftBuffer(column, index),
            "word-break": this.getWordBreak(column),
            ...tempStyle,
        };
    }

    getColStyle(column: DataTableColumn, index: number) {
        return {
            width: this.getWidth(column) || this.autoWidth,
            "min-width": this.getMinWidth(column),
            "max-width": this.getWidth(column) || this.autoMaxWidth,
        };
    }

    // iOS WebKit shrinks <col> widths to fit a width:100% table, so force the table to its full column width.
    // max() keeps the global min-width:100% so tables with few columns still fill the page.
    getTableMinWidth(): string {
        var totalWidth = this.columns
            .filter((column) => column.visible != false)
            .reduce((total, column) => {
                var width = this.getWidth(column) || this.autoWidth;
                return total + (/^\d+(\.\d+)?px$/.test(width) ? parseFloat(width) : 0);
            }, 0);

        return totalWidth > 0 ? `max(100%, ${totalWidth}px)` : null;
    }

    hasFooter(): boolean {
        if (this.columns.findIndex((x) => x.totalInFooter === true) > -1)
            return true;

        return false;
    }

    isRowSelected(item, columns: DataTableColumn[]) {
        var checkBoxColumns = columns.filter((x) => x.type == "CheckBox");

        for (var checkBoxColumn of checkBoxColumns) {
            if (item[checkBoxColumn.value] === true) {
                this.rowSelected = true;
                return true;
            }
        }

        this.rowSelected = false;
        return false;
    }

    isRowHighlighted(item, columns: DataTableColumn[]) {
        var isHighlighted =
            !this.isRowSelected(item, columns) &&
            item &&
            item["changeRowColour"] != null &&
            item["changeRowColour"] != "#ffffff";

        if (isHighlighted) this.highlightColour = item["changeRowColour"];

        return isHighlighted;
    }

    isTextTruncatable(item, column: DataTableColumn) {
        const value = item?.[column.value];
        return typeof value === "string" && value.length > this.textTruncateLength;
    }

    isTextExpanded(item, column: DataTableColumn) {
        return !!this.expandedTextCells.get(item)?.has(column.value);
    }

    getDisplayText(item, column: DataTableColumn) {
        const value = item?.[column.value];
        if (!this.isTextTruncatable(item, column) || this.isTextExpanded(item, column)) {
            return value;
        }
        return value.substring(0, this.textTruncateLength) + "...";
    }

    toggleTextExpanded(event: Event, item, column: DataTableColumn) {
        event.preventDefault();
        event.stopPropagation();
        let expanded = this.expandedTextCells.get(item);
        if (!expanded) {
            expanded = new Set<string>();
            this.expandedTextCells.set(item, expanded);
        }
        if (expanded.has(column.value)) {
            expanded.delete(column.value);
        } else {
            expanded.add(column.value);
        }
    }

    isColumnHighlighted(column: DataTableColumn, item) {
        var isHighlighted =
            !this.rowSelected &&
            item &&
            item[column.value + "_changeColour"] != null &&
            item[column.value + "_changeColour"] != "#ffffff";

        return isHighlighted;
    }

    getColumnHeaderClass(column: DataTableColumn) {
        let styleClass = column.styleClass || "";

        if (column.type == "CheckBox") styleClass += " text-start";

        if (this.hasOptions(column) && this.showExport)
            styleClass += " download-column";

        return styleClass;
    }

    areAnyColumnsFilterable(columns: DataTableColumn[]): boolean {
        let filteredColumns = (columns || []).filter(
            (column) => column.visible != false && column.filterable
        );

        return filteredColumns.length > 0;
    }

    getFooterStyle(column: DataTableColumn) {
        if (column.totalInFooter)
            return {
                "text-align": "right",
            };
    }

    optionsUpdated(menuItems: MenuItem[]) {
        this.menuItems = menuItems;
    }


    getSelectedValues(item, column) {
        return [];
    }

    getFileName(file) {
        //fileUrl will be null for entry from web
        if (file.fileUrl) {
            return file.fileName
        }

        return file.originalFileName
    }

    private groupPlantEntries() {
        var groupedItems = [];
        var group = null;
        this.dataset.sort((a, b) => b.reqId - a.reqId);

        this.dataset.forEach((x) => {
            if (group && x.groupFieldHeader != group) {
                groupedItems.push({});
            }

            groupedItems.push(x);
            group = x.groupFieldHeader;
        });

        this.dataset = groupedItems;
    }

    private refreshPlantEntries() {
        if (!this.selectedReqs || this.selectedReqs.length == 0) {
            this.dataset = this.plantEntries;
            this.groupPlantEntries();
            return;
        }

        this.dataset = this.plantEntries.filter((plant) => {
            return this.selectedReqs.some((req) => req == plant.reqId);
        });

        this.groupPlantEntries();
    }

    private searchTextOnPlant(searchValue) {
        this.searchValueText = searchValue
        this.dataset.forEach((entity) => {
            entity.selected = false;
        });
        if (!searchValue) {
            this.refreshPlantEntries();
            return;
        }

        searchValue = searchValue.toLowerCase();

        if (this.selectedReqs.length > 0) {
            this.refreshPlantEntries();
            this.searchReqIdFilteredPlantEntries = this.dataset;
        }

        this.dataset = this.searchReqIdFilteredPlantEntries.filter((x) => {
            let result: boolean = false;
            const iterate = (obj) => {
                Object.keys(obj).some((key) => {
                    if (!result && key !== "options" && obj[key] != null) {
                        if (typeof obj[key] === "object") {
                            iterate(obj[key]);
                        } else {
                            if (this.currentDataTableColumnList.find(x => x.value == key)) {
                                if (isNaN(obj[key]) && !isNaN(Date.parse(obj[key]))) {
                                    let currentDate = new DatePipe("en-US").transform(obj[key], "yyyy-MM-dd");
                                    if (currentDate.indexOf(searchValue) >= 0) {
                                        result = true;
                                        return true;
                                    }
                                }
                                else if (
                                    obj[key]
                                        .toString()
                                        .toLowerCase()
                                        .indexOf(searchValue) >= 0 &&
                                    obj[key].toString().toLowerCase().indexOf('data:image') == -1
                                ) {
                                    result = true;
                                    return true;
                                }
                            }
                        }
                    }
                });
            };
            iterate(x);
            return result;
        });
    }

    setMinMaxDate() {
        this.columns.map(col => {
            if (col.type == "Date") {
                col.min = col.min ? new Date(col.min) : col.min;
                col.max = col.max ? new Date(col.max) : col.max;
            }
        })
    }

    setYear() {
        const currentYear = new Date().getFullYear();
        this.yearRange = `${currentYear - 10}:${currentYear + 10}`;
    }
}
